import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, ReactNode } from 'react';
import gsap from 'gsap';
import { getA11ySettings, saveA11ySettings, A11ySettings } from './accessibility-settings';

interface AccessibilityContextType {
  strictMode: boolean;
  setStrictMode: (strict: boolean) => void;
}

const noop = () => undefined;

// Valeur par défaut hors provider (tests de composants isolés) : mode standard.
const AccessibilityContext = createContext<AccessibilityContextType>({
  strictMode: false,
  setStrictMode: noop,
});

export const AccessibilityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<A11ySettings>(() => getA11ySettings());

  const setStrictMode = useCallback((strictMode: boolean) => {
    setSettings((prev) => {
      const next = { ...prev, strictMode };
      saveA11ySettings(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ strictMode: settings.strictMode, setStrictMode }),
    [settings, setStrictMode]
  );

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
};

export function useAccessibility(): AccessibilityContextType {
  return useContext(AccessibilityContext);
}

/**
 * Applique le mode accessibilité renforcée au document tant que `active` est vrai :
 * `html[data-a11y="strict"]` (styles + `isMotionReduced()`) et timeline GSAP accélérée,
 * pour que les tweens finissent en une frame tout en résolvant leurs `onComplete`.
 * `useLayoutEffect` : posé avant les `useEffect` de montage des enfants.
 */
export function useStrictA11yDocument(active: boolean): void {
  useLayoutEffect(() => {
    if (!active || typeof document === 'undefined') return;
    const root = document.documentElement;
    root.dataset.a11y = 'strict';
    gsap.globalTimeline.timeScale(1000);
    return () => {
      delete root.dataset.a11y;
      gsap.globalTimeline.timeScale(1);
    };
  }, [active]);
}
